import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon, type IconName } from '@mobi/component-library/Assets/Icon/Icon'
import { RadioButton } from '../RadioButton'

type BadgeColor = 'success' | 'warning' | 'error' | 'info' | 'default'

type Props = {
  id: string
  isChecked: boolean
  children: ReactNode
  size: 'sm' | 'xs'
  isDisabled?: boolean
  iconName: IconName
  type?: RadioButtonType
  stackChildren?: boolean
  badgeText?: string
  badgeColor?: BadgeColor
  onClick?(): void
}

type RadioButtonType = React.ComponentProps<typeof RadioButton>['type']

export const RadioGroupItem: React.FC<Props> = ({
  id,
  isChecked,
  children,
  size,
  isDisabled = false,
  iconName,
  type,
  stackChildren = false,
  badgeText,
  badgeColor = 'default',
  onClick = () => null,
}) => {
  return (
    <WrapperStyled isChecked={isChecked} size={size} isDisabled={isDisabled} onClick={onClick}>
      <IconWrapper size={sizeMap[size].iconWrapperSize} isDisabled={isDisabled}>
        <Icon
          name={isDisabled ? 'LineInfoCircle' : iconName}
          type='cross'
          color={isDisabled ? colors.neutral['900'] : colors.lavender['600']}
          size={sizeMap[size].iconSize}
        />
      </IconWrapper>
      {stackChildren ? <ChildrenContainer>{children}</ChildrenContainer> : children}
      <RadioButton
        id={id}
        type={type ?? 'Radio'}
        isChecked={isChecked}
        size={sizeMap[size].radioButtonSize}
        isDisabled={isDisabled}
        onClick={onClick}
      />
      {badgeText ? (
        <Badge className='badge' color={badgeColorMap[badgeColor]}>
          {badgeText}
        </Badge>
      ) : null}
    </WrapperStyled>
  )
}

type SizeMap = Record<
  Props['size'],
  {
    radioButtonSize: 'sm' | 'md'
    iconWrapperSize: string
    iconSize: string
    wrapperPadding: string
  }
>

const sizeMap: SizeMap = {
  sm: {
    radioButtonSize: 'md',
    iconWrapperSize: '3.2rem',
    iconSize: '1.6rem',
    wrapperPadding: spacing.md,
  },
  xs: {
    radioButtonSize: 'sm',
    iconWrapperSize: '2.4rem',
    iconSize: '1.4rem',
    wrapperPadding: spacing.sm,
  },
}

type BadgeColorMap = Record<BadgeColor, string>

const badgeColorMap: BadgeColorMap = {
  success: colors.success[500],
  warning: colors.warning[500],
  error: colors.error[500],
  info: colors.mariner[500],
  default: colors.neutral[200],
}

const Badge = styled.div<{ color: string }>(({ color }) => ({
  position: 'absolute',
  top: '-1rem',
  right: '-1rem',
  fontStyle: 'italic',
  borderRadius: '2.4rem',
  backgroundColor: color,
  padding: '0 0.8rem',
  color: colors.white,
  fontSize: font.size.sm.fontSize,
  lineHeight: font.size.lg.lineHeight,
}))

const WrapperStyled = styled.div<Omit<Props, 'id' | 'iconName'>>(
  {
    display: 'grid',
    columnGap: spacing.sm,
    position: 'relative',
    gridTemplateColumns: 'auto 1fr auto auto',
    alignItems: 'center',
    fontFamily: font.family.primary,
    fontWeight: font.weight.medium,
    ...font.size.lg,
    color: colors.neutral['900'],
    borderRadius: radius.lgx1,
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral['200']}`,
    '&:focus-within': {
      outlineColor: colors.lavender['50'],
    },
    marginRight: '1rem',
  },
  ({ isChecked = true, size = 'sm', isDisabled = false }) => ({
    padding: sizeMap[size].wrapperPadding,
    ...(isChecked && {
      backgroundColor: colors.lavender['50'],
      border: `1px solid ${colors.lavender['300']}`,
    }),
    ...(isDisabled && {
      backgroundColor: colors.surface['100'],
      border: `1px solid ${colors.neutral['200']}`,
    }),
  })
)

const ChildrenContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

const IconWrapper = styled.div<{ size: string; isDisabled: boolean }>(({ size, isDisabled }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: radius.pill,
  backgroundColor: isDisabled ? colors.neutral['200'] : colors.lavender['100'],
  width: size,
  height: size,
}))

export default RadioGroupItem
